import { BoundaryPartRequestGeometryType } from 'generatedTypes/flight/models';
import { useEffect, useMemo, useRef, useState } from 'react';

import { MAP_LAYER } from 'components/Map/constants';
import useBoundary from 'components/Map/hooks/useBoundary';
import useBoundaryReset from 'components/Map/hooks/useBoundaryReset';
import useClick from 'components/Map/hooks/useClick';
import useDTO from 'components/Map/hooks/useDTO';
import useDrawingTool from 'components/Map/hooks/useDrawingTool';
import useFlightPath from 'components/Map/hooks/useFlightPaths';
import useFlyTo from 'components/Map/hooks/useFlyTo';
import useHomePointMarker from 'components/Map/hooks/useHomePointMarker';
import HomePointMarker from 'components/Map/hooks/useHomePointMarker/components/HomePointMarker';
import useLayerStyles from 'components/Map/hooks/useLayerStyles';
import useLayerVisibility from 'components/Map/hooks/useLayerVisibility';
import useMapContext from 'components/Map/hooks/useMapContext';
import useMapRecenter from 'components/Map/hooks/useMapRecenter';
import usePickPoint from 'components/Map/hooks/usePickPoint';
import usePrescriptionZones from 'components/Map/hooks/usePrescriptionZones';
import useReticle from 'components/Map/hooks/useReticle';
import useSelectFeature from 'components/Map/hooks/useSelectFeature';
import useStaticGeometry from 'components/Map/hooks/useStaticGeometry';
import useTranslation from 'components/Map/hooks/useTranslation';
import BoundaryPopover from 'components/MapBoundaryPopover';
import MapBox from 'components/MapBox';
import MapControlPopup from 'components/MapControlPopup';
import MapControlResetPopup from 'components/MapControlResetPopup';
import MapHorizontalControlBar from 'components/MapHorizontalControlBar';
import MapVerticalControlBar from 'components/MapVerticalControlBar';

import {
    EMPTY_PARTS,
    GEOJSON_PROPERTY,
    SINGLE_BOUNDARY_COUNT,
    STATIC_LAYER_IDS,
    STATIC_ZOOM,
    TEST_ID
} from './constants';
import type {
    CanvasPosition,
    Feature,
    Map,
    MapBoxConfig,
    Polygon,
    Position,
    Props,
    VertexPart
} from './types';

import styles from './styles.module.scss';

const MapInternal = ({
    className = '',
    clearRequestToken,
    commitRequestToken,
    drawingMode: controlledDrawingMode,
    drawingTool: controlledDrawingTool,
    flightPaths,
    flyToPoint = null,
    focusGeometry,
    geometry,
    homePoint,
    homePointMarkerAriaLabel,
    isBoundaryModified = false,
    isPickingPoint = false,
    mapInteractionOptions,
    mapOptions,
    onClickEditMap,
    onClose,
    onComplete,
    onFeatureSelect,
    onFlew,
    onPickPoint,
    prescriptionBounds = null,
    prescriptionZones = null,
    selectedFlight,
    telemetryPointsByFlightId,
    testId = TEST_ID
}: Props) => {
    const resizeObserverRef = useRef<ResizeObserver | null>(null);

    const [map, setMap] = useState<Map>();
    const [boundaryOptionPopoverOpen, setBoundaryOptionPopoverOpen] = useState(false);
    const [boundaryPopoverPosition, setBoundaryPopoverPosition] = useState<{
        x: number;
        y: number;
    }>();
    const [isDrawingInfoPopupOpen, setIsDrawingInfoPopupOpen] = useState(false);

    const { INCOMPLETE_BOUNDARY } = useTranslation();

    const { isEditable, parts } = geometry ?? {};
    const staticGeometryParts = isEditable ? EMPTY_PARTS : parts;
    const editableGeometryParts = isEditable ? parts : [];

    const vertexCoords = useMemo<VertexPart[]>(
        () =>
            (parts ?? [])
                .map((part, partIndex) => ({
                    coords: (part.boundary?.coordinates?.[0] ?? []) as Position[],
                    geometryType: part.geometryType,
                    id: `part-${partIndex}`,
                    partIndex
                }))
                .filter(part => part.geometryType !== BoundaryPartRequestGeometryType.CIRCLE)
                .map(({ coords, id, partIndex }) => ({ coords, id, partIndex })),
        [parts]
    );

    const {
        allowsDrawingWithoutControls = false,
        editMode,
        hasDrawingControls = false,
        hasLayerToggles = false,
        hasMapControls = false
    } = mapInteractionOptions;

    const { features, mapState, markers } = useMapContext();

    const { clear: clearFeatures, clearByLayer: clearFeaturesByLayer } = features;
    const { clear: clearMarkers, markerMap } = markers;

    const {
        drawingTool,
        isDrawing,
        pointMode,
        selectedFeature,
        setDrawingMode,
        setDrawingTool,
        setMapCenter,
        setMapErrors,
        setMousePosition,
        setSelectedFeature
    } = mapState;

    // Optional controlled-state sync: when the parent passes `drawingTool` /
    // `drawingMode` as props, mirror those values into the MapContext so
    // drawing hooks continue to read from a single source of truth.
    // Uncontrolled consumers (no prop passed) retain the existing internal
    // state behavior unchanged.
    useEffect(() => {
        if (controlledDrawingTool !== undefined) {
            setDrawingTool(controlledDrawingTool);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [controlledDrawingTool]);

    useEffect(() => {
        if (controlledDrawingMode !== undefined) {
            setDrawingMode(controlledDrawingMode);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [controlledDrawingMode]);

    const { boundaryPolygon, noEntryPolygons, removeBoundaryForFeature, updateBoundaryForFeature } =
        useBoundary();

    const { convertToDTO } = useDTO();

    const {
        getClusterPointStyle,
        getDrawingStyles,
        getStaticGeometryStyles,
        getUnclusteredPointStyle
    } = useLayerStyles();

    const { initializeStaticGeometry } = useStaticGeometry();
    const { highlightFlight, initializeFlightPaths } = useFlightPath();
    const { isBoundaryVisible, isFlightPathsVisible, toggleBoundary, toggleFlightPaths } =
        useLayerVisibility();

    const hasFlightPaths = (flightPaths?.length ?? 0) > 0;
    const hasBoundary = (staticGeometryParts?.length ?? 0) > 0;

    useEffect(() => {
        clearMarkers();
        clearFeatures();

        setSelectedFeature(undefined);
        setMapErrors([]);

        resetDrawingState();

        if (staticGeometryParts) {
            initializeStaticGeometry(staticGeometryParts);
        }

        if (editableGeometryParts) {
            loadExternalGeometry(editableGeometryParts);
        }

        initializeFlightPaths(flightPaths, telemetryPointsByFlightId);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditable, parts]);

    useEffect(() => {
        if (!map) {
            return;
        }

        const isSingleBoundary = (staticGeometryParts?.length ?? 0) <= SINGLE_BOUNDARY_COUNT;
        const minZoom = isSingleBoundary ? 0 : STATIC_ZOOM.min;

        for (const layerId of STATIC_LAYER_IDS) {
            if (map.getLayer(layerId)) {
                map.setLayerZoomRange(layerId, minZoom, STATIC_ZOOM.max);
            }
        }
    }, [map, staticGeometryParts]);

    // Toggle the "modified" dashed outline overlay on the APPLY polygon. The
    // layer is always registered by useLayerStyles so we can flip its
    // visibility rather than adding/removing it at runtime.
    useEffect(() => {
        if (!map) {
            return;
        }

        if (!map.getLayer(MAP_LAYER.STATIC_BOUNDARY_OUTLINE_MODIFIED)) {
            return;
        }

        const visibility = isBoundaryModified ? 'visible' : 'none';

        map.setLayoutProperty(MAP_LAYER.STATIC_BOUNDARY_OUTLINE_MODIFIED, 'visibility', visibility);
    }, [map, isBoundaryModified]);

    // Pick-point mode: attach a dedicated click listener that bypasses the
    // useClick pipeline. See Decision 43.
    usePickPoint({ map, onPickPoint });

    // Imperative map centering: when the parent passes a fresh `flyToPoint`,
    // the hook calls `map.flyTo(...)` and then invokes `onFlew` so the parent
    // can null the trigger back out (one-shot pattern).
    useFlyTo({ map, onFlew, point: flyToPoint });

    // Home-point map marker: renders a single pin at `homePoint` when set.
    // The hook short-circuits on falsy homePoint, so the null markerElement
    // branch is safe and avoids constructing a node that is never used.
    useHomePointMarker({
        homePoint: homePoint ?? null,
        map,
        markerElement: homePoint ? (
            <HomePointMarker ariaLabel={homePointMarkerAriaLabel ?? ''} />
        ) : null
    });

    // Variable-rate prescription zones: render shapefile-derived polygons via
    // the shared feature store. Hook is null-safe — it clears any previously
    // added features and exits when zones or bounds are null.
    usePrescriptionZones({
        bounds: prescriptionBounds,
        zones: prescriptionZones
    });

    // Swap the Mapbox cursor to a crosshair while pick mode is active so the
    // user sees that the next click will commit the home point. The cleanup
    // restores the cursor when the effect re-runs or the component unmounts.
    useEffect(() => {
        if (!map) {
            return;
        }

        const canvas = map.getCanvas();

        canvas.style.cursor = isPickingPoint ? 'crosshair' : '';

        return () => {
            canvas.style.cursor = '';
        };
    }, [map, isPickingPoint]);

    useEffect(() => {
        // Clear previous flight path features before initializing the new ones so
        // successive replans do not accumulate stale geometry on the map.
        clearFeaturesByLayer(MAP_LAYER.FLIGHT_PATH_LINE);
        clearFeaturesByLayer(MAP_LAYER.FLIGHT_PATH_FILL);
        initializeFlightPaths(flightPaths, telemetryPointsByFlightId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flightPaths, telemetryPointsByFlightId]);

    useEffect(() => {
        highlightFlight(selectedFlight);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFlight]);

    const {
        clearUndoRedo,
        handleClick,
        handleCompleteDrawing,
        handleLoadDrawing,
        handleResetDrawing,
        loadExternalGeometry,
        redo,
        resetDrawingState,
        undo
    } = useDrawingTool({
        onDrawingCompleted: feature => {
            handleDrawingCompleted(feature);
        },
        onDrawingUpdated: feature => {
            handleDrawingUpdated(feature);
        }
    });

    useReticle();

    useSelectFeature({
        map,
        onDrawingComplete: handleCompleteDrawing,
        onDrawingLoaded: handleLoadDrawing
    });

    const { handleMapClick } = useClick({
        boundaryPolygon,
        editMode,
        isDrawing,
        noEntryPolygons,
        onClick: handleClick,
        onClickBoundaryLabel: position => {
            setBoundaryOptionPopoverOpen(true);

            const projected = map?.project(position as [number, number]);

            setBoundaryPopoverPosition(projected);
        },
        removeBoundaryForFeature
    });

    const { centerOnVisibleArtifacts, handleChangeMapCenter } = useMapRecenter({
        flightPaths,
        focusGeometry,
        geometry,
        isBoundaryVisible,
        isFlightPathsVisible,
        map,
        mapOptions
    });

    const handleToggleFlightPaths = () => {
        const newVisible = toggleFlightPaths(map);

        centerOnVisibleArtifacts({ isBoundaryVisible, isFlightPathsVisible: newVisible });
    };

    const handleToggleBoundary = () => {
        const newVisible = toggleBoundary(map);

        centerOnVisibleArtifacts({ isBoundaryVisible: newVisible, isFlightPathsVisible });
    };

    const handleClearDrawing = () => {
        handleResetDrawing();
        setMapErrors([]);

        if (selectedFeature) {
            features.removeFeature(selectedFeature.id as string);
            setSelectedFeature(undefined);

            removeBoundaryForFeature(selectedFeature.id as string);

            return;
        }
    };

    const handleCompleteClicked = () => {
        if (isDrawing) {
            handleCompleteDrawing();

            return;
        }

        if (!boundaryPolygon) {
            const incompleteBoundaryError = new Error(INCOMPLETE_BOUNDARY);

            setMapErrors([incompleteBoundaryError]);

            return;
        }

        const dto = convertToDTO(boundaryPolygon, noEntryPolygons);

        onComplete?.(dto);

        if (selectedFeature) {
            selectedFeature.properties = {
                ...selectedFeature.properties,
                [GEOJSON_PROPERTY.IS_SELECTED]: false
            };
            setSelectedFeature(undefined);
        }
    };

    // Counter-token observers (Decision 46). The parent (e.g. Flight Planner)
    // triggers commit / clear imperatively by incrementing a scalar counter.
    // Each effect ref-guards an initial-mount skip so remounting the component
    // with a non-zero counter does not auto-fire. The commit effect additionally
    // gates on `isDrawing` (Decision 47) — "Done editing" is a pure exit when
    // there is nothing to finalize.
    //
    // Handler refs hold the latest closure so the effects can invoke them
    // without listing them in dep arrays. The ref is mutated on every render
    // (outside any useEffect) — safe because it is never itself a dependency.
    const handleCompleteClickedRef = useRef(handleCompleteClicked);

    handleCompleteClickedRef.current = handleCompleteClicked;

    const handleClearDrawingRef = useRef(handleClearDrawing);

    handleClearDrawingRef.current = handleClearDrawing;

    const lastCommitTokenRef = useRef<number | undefined>(commitRequestToken);

    useEffect(() => {
        if (commitRequestToken === undefined) {
            return;
        }

        if (lastCommitTokenRef.current === commitRequestToken) {
            return;
        }

        lastCommitTokenRef.current = commitRequestToken;

        if (!isDrawing) {
            return;
        }

        handleCompleteClickedRef.current();
    }, [commitRequestToken, isDrawing]);

    const lastClearTokenRef = useRef<number | undefined>(clearRequestToken);

    useEffect(() => {
        if (clearRequestToken === undefined) {
            return;
        }

        if (lastClearTokenRef.current === clearRequestToken) {
            return;
        }

        lastClearTokenRef.current = clearRequestToken;

        handleClearDrawingRef.current();
    }, [clearRequestToken]);

    const {
        handleDrawingModeChange,
        handleDrawingToolChange,
        handleExitDrawing,
        handlePointModeChange,
        isResetPopupOpen,
        resetState
    } = useBoundaryReset({
        onCompleteDrawing: handleCompleteClicked,
        onDrawingReset: handleClearDrawing,
        onExitDrawing: onClose
    });

    const handleDrawingCompleted = (feature: Feature<Polygon>) => {
        updateBoundaryForFeature(feature);

        if (selectedFeature?.id === feature.id) {
            handleLoadDrawing(feature);
        } else {
            clearUndoRedo();
        }
    };

    const handleDrawingUpdated = (feature: Feature<Polygon>) => {
        updateBoundaryForFeature(feature);
    };

    const getMapboxConfig = (): MapBoxConfig => {
        const { accessToken, center, mapStyle, maxZoom, minZoom, zoom } = mapOptions;

        return {
            accessToken,
            center,
            layerStyles: {
                getClusterStyle: getClusterPointStyle,
                getDrawingStyles: getDrawingStyles,
                getStaticGeometryStyles: getStaticGeometryStyles,
                getUnclusteredStyle: getUnclusteredPointStyle
            },
            mapStyle,
            maxZoom,
            minZoom,
            zoom
        };
    };

    useEffect(() => {
        setIsDrawingInfoPopupOpen(true);
    }, [pointMode, drawingTool]);

    const handleMapLoad = (loadedMap: Map) => {
        setMap(loadedMap);

        const mapContainer = loadedMap.getContainer();

        if (!mapContainer) {
            return;
        }

        if (!resizeObserverRef.current) {
            resizeObserverRef.current = new ResizeObserver(() => {
                requestAnimationFrame(() => {
                    if (!loadedMap.getCanvas()) {
                        return;
                    }

                    loadedMap.resize();
                });
            });
        }

        resizeObserverRef.current.observe(mapContainer);
    };

    useEffect(
        () => () => {
            if (resizeObserverRef.current) {
                resizeObserverRef.current.disconnect();
                resizeObserverRef.current = null;
            }
        },
        []
    );

    // Approach A (see Decision 48): `useClick.handleMapClick` early-returns on
    // `!hasDrawingControls`. To keep every existing consumer unchanged, we
    // expand the gate at the call site — when a consumer opts in via
    // `allowsDrawingWithoutControls`, clicks flow through `useClick` even though
    // the built-in `MapHorizontalControlBar` is not mounted.
    const clickControlsEnabled = hasDrawingControls || allowsDrawingWithoutControls;

    const isMapControlPopupEnabled =
        hasDrawingControls && !isResetPopupOpen && isDrawingInfoPopupOpen;
    const isResetPopupEnabled = hasDrawingControls && isResetPopupOpen;

    return (
        <>
            <MapBox
                onClick={(markerId?: string) =>
                    handleMapClick({
                        hasDrawingControls: clickControlsEnabled,
                        map,
                        markerId
                    })
                }
                className={`${styles.mapbox} ${className}`}
                config={getMapboxConfig()}
                geojson={features.features}
                markers={Object.values(markerMap)}
                onDoubleClick={isDrawing ? handleCompleteClicked : undefined}
                onFeatureSelect={onFeatureSelect}
                onLoad={handleMapLoad}
                onMouseMove={setMousePosition}
                onMove={setMapCenter}
                selectedFlightId={selectedFlight?.id}
                testId={testId}
                vertexCoords={vertexCoords}>
                {boundaryOptionPopoverOpen && (
                    <BoundaryPopover
                        onClose={() => {
                            setBoundaryOptionPopoverOpen(false);
                            setBoundaryPopoverPosition(undefined);
                        }}
                        canvasPosition={boundaryPopoverPosition as CanvasPosition}
                        isOpen={boundaryOptionPopoverOpen}
                        onDelete={handleClearDrawing}
                    />
                )}
            </MapBox>

            {hasDrawingControls && (
                <MapHorizontalControlBar
                    className={styles.horizontalControlBar}
                    onChangeDrawingMode={handleDrawingModeChange}
                    onChangeDrawingTool={handleDrawingToolChange}
                    onChangePointMode={handlePointModeChange}
                    onClickClose={handleExitDrawing}
                    onClickCompleteDrawing={handleCompleteClicked}
                    onClickRecenter={handleChangeMapCenter}
                    onClickRedo={redo}
                    onClickUndo={undo}
                />
            )}

            {hasMapControls && (
                <MapVerticalControlBar
                    layerToggles={
                        hasLayerToggles && !hasDrawingControls
                            ? {
                                  hasBoundary,
                                  hasFlightPaths,
                                  isBoundaryVisible,
                                  isFlightPathsVisible,
                                  onToggleBoundary: handleToggleBoundary,
                                  onToggleFlightPaths: handleToggleFlightPaths
                              }
                            : undefined
                    }
                    className={styles.verticalControlBar}
                    isDrawingEnabled={hasDrawingControls}
                    onClickEditMap={onClickEditMap}
                    onClickRecenterMap={handleChangeMapCenter}
                />
            )}

            {isMapControlPopupEnabled && (
                <MapControlPopup
                    onClickAddPoint={() => {
                        handleMapClick({
                            hasDrawingControls: clickControlsEnabled,
                            map
                        });
                    }}
                    onClickDismiss={() => {
                        setIsDrawingInfoPopupOpen(false);
                    }}
                />
            )}

            {isResetPopupEnabled && <MapControlResetPopup resetState={resetState} />}
        </>
    );
};

export default MapInternal;
